// ScanVault keeps the scanner's per-source CVSS fields (nvd.V3Vector,
// redhat.V3Score, ...). The CVE drawers expect one parsed CVSS v3 record.

const sourcePreference = ['nvd', 'ghsa'];

const metricLabels: Record<string, Record<string, string>> = {
  AV: { N: 'Network', A: 'Adjacent', L: 'Local', P: 'Physical' },
  AC: { L: 'Low', H: 'High' },
  PR: { N: 'None', L: 'Low', H: 'High' },
  UI: { N: 'None', R: 'Required' },
  C: { N: 'None', L: 'Low', H: 'High' },
  I: { N: 'None', L: 'Low', H: 'High' },
  A: { N: 'None', L: 'Low', H: 'High' },
};

const weights = {
  AV: { N: 0.85, A: 0.62, L: 0.55, P: 0.2 },
  AC: { L: 0.77, H: 0.44 },
  UI: { N: 0.85, R: 0.62 },
  CIA: { H: 0.56, L: 0.22, N: 0 },
};

// Sub-scores are shown to one decimal; only the base score rounds up.
const round1 = (value: number) => Math.round(value * 10) / 10;

/** Picks NVD's v3 vector, else the source whose v3 score matches `score`. */
export function pickCvssV3Vector(
  cvss: Record<string, string> = {},
  score?: number,
  fallback?: string,
) {
  const vectors = Object.entries(cvss).filter(
    ([key, value]) => /\.V3Vector$/i.test(key) && value?.startsWith('CVSS:3'),
  );
  const source = (key: string) => key.split('.')[0].toLowerCase();
  for (const preferred of sourcePreference) {
    const match = vectors.find(([key]) => source(key) === preferred);
    if (match) return match[1];
  }
  const matchingScore = vectors.find(
    ([key]) =>
      score !== undefined &&
      Number(cvss[key.replace(/V3Vector$/i, 'V3Score')]) === score,
  );
  return matchingScore?.[1] ?? vectors[0]?.[1] ?? fallback ?? '';
}

/** Parses a CVSS 3.x base vector into the fields the CVE drawers show. */
export function parseCvssV3Vector(vector?: string) {
  if (!vector?.startsWith('CVSS:3')) return {};
  const metrics: Record<string, string> = {};
  vector
    .split('/')
    .slice(1)
    .forEach(part => {
      const [key, value] = part.split(':');
      if (key && value) metrics[key] = value;
    });
  const result: Record<string, string | number> = { vector };
  const label = (key: string) => metricLabels[key]?.[metrics[key]];
  if (label('AV')) result.attack_vector = label('AV');
  if (label('AC')) result.attack_complexity = label('AC');
  if (label('PR')) result.privileges_required = label('PR');
  if (label('UI')) result.user_interaction = label('UI');
  if (label('C')) result.confidentiality_impact = label('C');
  if (label('I')) result.integrity_impact = label('I');
  if (label('A')) result.availability_impact = label('A');

  const scopeChanged = metrics.S === 'C';
  const pr = {
    N: 0.85,
    L: scopeChanged ? 0.68 : 0.62,
    H: scopeChanged ? 0.5 : 0.27,
  }[metrics.PR];
  const av = weights.AV[metrics.AV];
  const ac = weights.AC[metrics.AC];
  const ui = weights.UI[metrics.UI];
  const [c, i, a] = ['C', 'I', 'A'].map(key => weights.CIA[metrics[key]]);
  if ([av, ac, pr, ui, c, i, a].every(value => value !== undefined)) {
    const iss = 1 - (1 - c) * (1 - i) * (1 - a);
    const impact = scopeChanged
      ? 7.52 * (iss - 0.029) - 3.25 * Math.pow(iss - 0.02, 15)
      : 6.42 * iss;
    result.impact_score = round1(Math.max(impact, 0));
    result.exploitability_score = round1(8.22 * av * ac * pr * ui);
  }
  return result;
}

/** The parsed CVSS v3 record for a ScanVault CVE catalog item. */
export function scanVaultCvssV3(item: {
  cvss?: Record<string, string>;
  cvss_score?: number;
  cvss_vector?: string;
}) {
  const vector = item.cvss_vector?.startsWith('CVSS:3')
    ? item.cvss_vector
    : pickCvssV3Vector(item.cvss, item.cvss_score, item.cvss_vector);
  return {
    ...(item.cvss ?? {}),
    ...parseCvssV3Vector(vector),
    vector: vector || item.cvss_vector,
    score: item.cvss?.score ?? item.cvss_score,
  };
}
